const NS = 'ChartBlock';
const CLS = 'chartBlock';

let d3 = require('d3');
let uuidv4 = require('uuid/v4');
let moment = require('moment');

import { mediaQuery } from '../../../../global/js/wool/mediaQuery';
import { numberFormatter } from '../../../../global/js/wool/numberFormatter';
import { colorGenerator } from '../../../../global/js/wool/colorGenerator';

export class ChartBlock {
	constructor(element, className = CLS) {
		this.element = element;
		this.$element = $(element);
		this.$chart = this.$element.find(`.chart`);

		this.setChartId();
		this.setEmptyMessages();
		this.setEmptyData();

		this.isDataEmpty = false;
		this.margin = {
			top: 30,
			right: 30,
			bottom: 50,
			left: 50
		};

		this.mediaQuery = mediaQuery;
		this.numberFormatter = numberFormatter;
		this.colorGenerator = colorGenerator;

		this.heightRatio = 0.66; // Height ratio relative to width, in this case it would be 3:2

		// Updates the chart from an external source
		this.$element.on(`${className}.update`, (event, params) => {
			this.updateChartData(params);
		});
	}

	updateChartData(params) {
		let data = params.data;

		if(!_.isNil(data)) {
			this.isDataEmpty = false;
			this.data = data;
			this.removeMessage();
			this.initChart();
		}
		else {
			if(!params.isEmpty) {
				console.error(`${NS}::Error`, 'Chart data could not be loaded.');
			}
			this.isDataEmpty = true;
			this.data = _.cloneDeep(this.emptyData);
			this.initChart();
			this.removeMessage();

			if(params.isEmpty) {
				this.appendMessage(false);
			}
			else {
				this.appendMessage(true);
			}
		}
	}

	// Ensures that the chart contains a unique id
	setChartId() {
		this.chartId = uuidv4();
		this.$chart.attr('data-uuid', this.chartId);
	}

	getChartNode() {
		return d3.select(`[data-uuid="${this.chartId}"]`);
	}

	// Sets the generic error messages
	setEmptyMessages() {
		this.emptyMessage = !_.isNil(this.$chart.attr('data-empty-message')) ? this.$chart.attr('data-empty-message') : 'No items loaded';
		this.errorMessage = !_.isNil(this.$chart.attr('data-error-message')) ? this.$chart.attr('data-error-message') : 'Error in loading chart…';
	}

	// Sets empty data for the chart to render. Overridable
	setEmptyData() {
		let values = [];

		for(let i = 0; i < 6; i++) {
			values.push({
				xValue: i + 1,
				yValue: i + 1
			});
		}

		this.emptyData = values;
	}

	appendMessage(isError) {
		let emptyMessage = '';
		if(isError) {
			emptyMessage = this.errorMessage;
		}
		else {
			emptyMessage = this.emptyMessage;
		}

		this.$chart.append(`<div class="${CLS}-chart-message chart-message ${isError ? `${CLS}-chart-message--error` : ''}">${emptyMessage}</div>`);
	}

	removeMessage() {
		this.$chart.remove(`.chart-message`);
	}

	// Initializes the chart by revealing it, drawing it and implementing an event that redraws it when the window resizes
	initChart() {
		this.$chart.css({ visibility: 'visible' });
		this.drawChart();
		$(window).on('resize', _.debounce(() => {
			this.drawChart();
		}, 500));
	}

	// Overridable draw chart function
	drawChart() {
		this.clearSvg();
		this.setChartDimensions();
		this.drawSvg();

		this.defineXAxesScales();
		this.defineYAxesScales();

		this.drawXAxes();
		this.drawYAxes();
	}

	clearSvg() {
		d3.select(`[data-uuid="${this.chartId}"]`).select('svg').remove(); // Removes the chart
	}

	setChartDimensions() {
		this.width = this.$element.width() - this.margin.left - this.margin.right;
		this.height = (this.$element.width() * this.heightRatio) - this.margin.top - this.margin.bottom;
	}

	drawSvg() {
		this.svg = d3.select(`[data-uuid="${this.chartId}"]`)
			.append('svg')
			.attr('width', this.width + this.margin.left + this.margin.right)
			.attr('height', this.height + this.margin.top + this.margin.bottom)
			.append('g')
			.attr('transform', `translate(${this.margin.left},${this.margin.top})`);
	}

	defineXAxesScales() {
		this.xAxisBottom = d3.scaleLinear()
			.range([0, this.width])
			.domain(d3.extent(this.data, (datum) => {
				return datum.xValue;
			})).nice();
	}

	defineYAxesScales() {
		this.yAxisLeft = d3.scaleLinear()
			.range([this.height, 0])
			.domain(d3.extent(this.data, (datum) => {
				return datum.yValue;
			})).nice();
	}

	drawXAxes() {
		this.svg.append('g')
			.attr('class', 'x-axis x-axis--bottom')
			.attr('transform', `translate(0, ${this.height})`)
			.call(d3.axisBottom(this.xAxisBottom));
	}

	drawYAxes() {
		this.svg.append('g')
			.attr('class', 'y-axis')
			.call(d3.axisLeft(this.yAxisLeft));
	}

	// Util method to format moment dates
	getMomentDate(date, format = 'DD/MM/YYYY') {
		return moment(date, format);
	}

	// Creates an inner tspan element for the text element and clips the text
	textWrap(textElement, maxWidth, x, y) {
		let words = textElement.text();
		let tspan = textElement.text(null).append('tspan').text(words).attr('x', x).attr('y', y);

		let textLength = tspan.node().getComputedTextLength();
		let text = tspan.text();
		while(textLength > maxWidth && text.length > 0) {
			text = text.slice(0, -1);
			tspan.text(text + '…');
			textLength = tspan.node().getComputedTextLength();
		}
	}

	// Gets an appropriate tick interval depending on the range
	getLinearTick(range, power = 0, tickCount = 20) {
		if(range !== 0) {
			let intervals = [0.1, 0.2, 0.5, 1, 2, 5];
			for(let i = 0; i < intervals.length; i++) {
				if((range / (intervals[i] * Math.pow(10, power))) > tickCount) {
					continue;
				}
				else {
					return intervals[i] * Math.pow(10, power);
				}
			}
			power += 1;
			return this.getLinearTick(range, power, tickCount);
		}
		return 1;
	}
}
